/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import * as Sentry from '@sentry/browser';
import appSettings from '../config/settings';

/**
 * Errors Sentry should drop before transmission.
 *
 * 🔴 DO NOT add 'Loading chunk' or 'Failed to fetch dynamically imported
 * module' here, even though most published ignoreErrors starter lists include
 * them. That is the ChunkLoadError stale-build signal — a deploy strands an
 * already-open wizard tab, LoadableComponentErrorBoundary catches the failed
 * chunk import and reports it via log.onRecoverableError below. That defect
 * cost a real seller three attempts on 2026-08-05 and was invisible until
 * Sentry went live on 2026-08-06. Suppressing it would blind us to the one bug
 * we most need to see. Matching here is substring-based for strings, so a
 * pattern that merely CONTAINS those phrases would silence it too.
 *
 * Sentry merges its own DEFAULT_IGNORE_ERRORS list into whatever we pass
 * (@sentry/core eventFilters.js `_mergeOptions`), so entries already covered
 * there — 'Script error.', the ResizeObserver "undelivered notifications"
 * variant, the googletag/solana browser-extension noise — are deliberately not
 * repeated below.
 */
const ingoreErrorsMap = {
  ['ResizeObserver loop limit exceeded']: true, // Some exotic browsers seems to emit these.
  ['Error reading']: true, // Ignore file reader errors (ImageFromFile)
  ['AxiosError: Network Error']: true,
  // A browser-initiated XHR teardown: the visitor navigated away, closed the
  // tab or lost signal while a Sharetribe SDK request was still in flight. The
  // SDK bundles axios, whose xhr adapter rejects from `onabort` with
  // ECONNABORTED / "Request aborted"; that rejection reaches Sentry through our
  // OWN log.error (observed tag code:app-asset-fetch-failed —
  // hostedAssets.duck.js:122, the boot-time assets fetch every page load makes),
  // so it arrives handled:yes rather than as an unhandled rejection.
  //
  // This is NOT a cancel we performed. A deliberate AbortController/cancelToken
  // cancel settles the promise with CanceledError ("canceled") BEFORE calling
  // abort(), so it would surface under a different name entirely. Nothing in
  // this app cancels an SDK request anyway. Unactionable either way — the
  // request was already irrelevant by the time it failed.
  //
  // Sibling of the 'AxiosError: Network Error' entry above.
  ['AxiosError: Request aborted']: true,
};

// Patterns that cannot live in the string-keyed map above, because a RegExp
// would stringify if used as an object key.
const ignoreErrorPatterns = [
  // Generalises Sentry's own DEFAULT_IGNORE_ERRORS entry, which is anchored to
  // `MethodName:simulateEvent` and therefore did NOT match the
  // `MethodName:update` variant we received on 2026-08-06.
  //
  // The string appears nowhere in our source and in no dependency except inside
  // @sentry/core's own filter list, so it does not originate in our stack.
  // Sentry attributes the family to CEFSharp (a .NET Chromium embedder); our
  // event carried an "antifingerprint not defined yet" console breadcrumb, i.e.
  // an extension injecting a script into the page. It arrived handled:no with
  // mechanism auto.browser.global_handlers.onunhandledrejection and NO
  // stacktrace — the rejection reason was a bare string — which is also why
  // denyUrls could not filter it and ignoreErrors is the only lever.
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
];

const pickSelectedErrors = (ignored, entry) => {
  const [key, value] = entry;
  return value === true ? [...ignored, key] : ignored;
};

/**
 * The resolved list handed to Sentry.init. Exported so tests can assert on the
 * entries directly, rather than only observing their effect.
 */
export const ignoreErrors = [
  ...Object.entries(ingoreErrorsMap).reduce(pickSelectedErrors, []),
  ...ignoreErrorPatterns,
];

/**
 * Story 3.10 AC6.4 — hashed-never-raw, applied to the browser SDK.
 *
 * The recover-password link prefills with `?email=<address>`
 * (AuthenticationPage.js:128). The browser SDK has no `include` options like
 * the Node side — HttpContext is a default integration and unconditionally
 * attaches the page URL and Referer — so the equivalent fix is a beforeSend.
 *
 * The URL-carrying fields below were NOT reasoned about; they were found by
 * capturing a real envelope from a real client under jsdom on
 * /recover-password?email=… and reporting every JSON path the address landed
 * on. That surfaced two carriers beyond the page URL and Referer: breadcrumbs
 * (navigation `from`/`to` and fetch/xhr `url`) and stack-frame filenames — the
 * latter because an error thrown from an inline <script> is attributed to the
 * document URL, query string and all.
 *
 * Not covered: span-level url data on tracing transactions. This app sets no
 * tracesSampleRate, so no transaction is ever produced and the gap is latent
 * rather than live.
 *
 * Tracing is off deliberately, not by oversight. Switching it on would put the
 * `?email=` prefill onto a span-data path this beforeSend does not reach, so
 * extending the scrub to span data is a PREREQUISITE of enabling tracing, not a
 * follow-up to it — Story 3.10 AC6.4 forbids the intermediate state. The rate
 * itself is an operator call besides: a flat few percent versus a rate-limited
 * tracesSampler, so a traffic spike cannot become a Sentry bill.
 *
 * Tracked as deferred-work #728. It is an open decision with a stated
 * prerequisite, not a switch nobody has got round to flipping.
 */
const stripUrlQuery = url => (typeof url === 'string' ? url.split('?')[0].split('#')[0] : url);

// Only writes back when there was a string there to begin with, so the scrub
// never introduces keys (an `abs_path: undefined` on every frame) that the
// event did not already have.
const stripInPlace = (obj, key) => {
  if (obj && typeof obj[key] === 'string') {
    obj[key] = stripUrlQuery(obj[key]);
  }
};

/**
 * Story 11.12 AC-A2 (code review HIGH-1, 2026-08-31) — 🔴 STRIPPING THE QUERY IS
 * NOT ENOUGH FOR AN S3 OBJECT URL. THE FORBIDDEN DATA IS IN THE PATH.
 *
 * `stripUrlQuery` above removes `?X-Amz-Signature=…&X-Amz-Credential=…` — the
 * signature and the token. It leaves the path, and for the seller-video bucket
 * the path IS the objectKey:
 *
 *   https://<bucket>.s3.<region>.amazonaws.com/seller-video-intake/<sellerUuid>/<ts>-<rand>-<filename>
 *   ^ kept by stripUrlQuery ─────────────────────────────────────────────────^
 *
 * (tph-api `src/services/s3Presign.js:180` composes the key; `sanitizeFilename`
 * only maps characters outside `[A-Za-z0-9._-]` to `_`, so the seller's filename
 * survives it near-verbatim, and sellers name files after horses and people.)
 *
 * Sentry's breadcrumbs integration records the URL of every XHR
 * (`@sentry-internal/browser-utils` `instrument/xhr.js` captures it at `open()`
 * and emits on `readyState === 4`), so the presigned PUT that Story 11.4's
 * `putFileToS3` makes lands in the breadcrumb buffer. From there it rides on
 * ANY event captured afterwards on that page.
 *
 * 🔴 SCOPED DELIBERATELY NARROW. This rewrites ONE thing: a breadcrumb `url`
 * whose host is an S3 object host. Every other breadcrumb in the product is
 * untouched, and the ORIGIN is deliberately kept — the bucket host is not
 * customer data, and "the PUT to the video bucket failed" is the diagnostic the
 * seam exists to deliver. What goes is the path and the query: the objectKey,
 * the seller uuid, the filename, the signature and the token, together.
 *
 * A relative URL is returned untouched: it cannot be an S3 object URL, and
 * clobbering one would be exactly the blanket change this is not.
 */
const S3_OBJECT_HOST = /(^|\.)s3([.-][a-z0-9-]+)?\.amazonaws\.com$/i;

export const redactS3ObjectUrl = url => {
  if (typeof url !== 'string' || !url) {
    return url;
  }
  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    // Relative, or not a URL at all. Never an S3 object URL — leave it alone.
    return url;
  }
  return S3_OBJECT_HOST.test(parsed.hostname) ? `${parsed.origin}/[redacted]` : url;
};

/**
 * Story 11.12 AC-A2 — the `beforeBreadcrumb` seam, so the redaction happens when
 * the breadcrumb is RECORDED rather than when an event is sent. Nothing is
 * dropped: the breadcrumb still says a PUT to the video bucket returned 403.
 */
export const scrubBreadcrumb = breadcrumb => {
  const data = breadcrumb && breadcrumb.data;
  if (data && typeof data.url === 'string') {
    data.url = redactS3ObjectUrl(data.url);
  }
  return breadcrumb;
};

export const scrubEventUrls = event => {
  if (!event) {
    return event;
  }

  if (event.request) {
    stripInPlace(event.request, 'url');

    // HttpContext sets `Referer` (one 'r'); match defensively regardless of
    // casing in case another integration writes its own spelling.
    const headers = event.request.headers;
    if (headers) {
      Object.keys(headers).forEach(key => {
        if (/^referer$/i.test(key)) {
          stripInPlace(headers, key);
        }
      });
    }
  }

  // Navigation breadcrumbs carry `from`/`to`; fetch and xhr ones carry `url`.
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs.forEach(crumb => {
      const data = crumb && crumb.data;
      if (data) {
        // Story 11.12 AC-A2 — belt and braces. `beforeBreadcrumb` above already
        // redacted this at record time; repeating it here means the event is
        // clean even for a breadcrumb that reached the buffer by some other
        // route. It is the same one-host rule, not a second policy.
        if (typeof data.url === 'string') {
          data.url = redactS3ObjectUrl(data.url);
        }
        ['url', 'to', 'from'].forEach(key => stripInPlace(data, key));
      }
    });
  }

  // An inline-script error is attributed to the document URL.
  const values = event.exception && event.exception.values;
  if (Array.isArray(values)) {
    values.forEach(value => {
      const frames = value && value.stacktrace && value.stacktrace.frames;
      if (Array.isArray(frames)) {
        frames.forEach(frame => {
          stripInPlace(frame, 'filename');
          stripInPlace(frame, 'abs_path');
        });
      }
    });
  }

  return event;
};

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed.
 */
export const setup = () => {
  if (appSettings.sentryDsn) {
    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: appSettings.sentryDsn,
      environment: appSettings.env,
      ignoreErrors,
      // Story 3.10 AC6.4 — strips the `?email=` prefill (and any other query)
      // off every URL-carrying field. See scrubEventUrls above.
      beforeSend: scrubEventUrls,
      beforeSendTransaction: scrubEventUrls,
      // Story 11.12 AC-A2 — redacts the path of a seller-video S3 object URL at
      // the moment the breadcrumb is recorded. See redactS3ObjectUrl above for
      // why the query strip alone leaves the objectKey and the filename behind.
      beforeBreadcrumb: scrubBreadcrumb,
    });
  }
};

/**
 * Story 9.17 (OQ4) — add a Sentry breadcrumb.
 *
 * Breadcrumb ONLY: no alert, no pager (operator decision, on record). Used by
 * the email-status degrade path, whose failure is deliberately invisible to the
 * user (AC5.3) and — for a CORS block — invisible server-side too
 * (deferred-work #497). This is the only trace such a failure leaves anywhere,
 * so it exists to be findable after the fact, not to wake anyone up.
 *
 * A no-op when no DSN is configured, since Sentry.init never ran.
 *
 * @param {String} category
 * @param {String} message
 * @param {Object} data — must never carry an email address or any PII.
 */
export const breadcrumb = (category, message, data) => {
  Sentry.addBreadcrumb({ category, message, level: 'warning', data });
};

/**
 * Story 3.24 pass B — a deliberate, non-exception signal: the SAME shape
 * tph-api's custom alerts use (`carrierVerdict.js` `reportSignal`, the
 * `relay_video_host_stranded` detector) — `Sentry.captureMessage(message,
 * { level, tags: { story, stage, signal }, fingerprint, extra })`. A constant
 * message and a per-signal fingerprint make a recurring condition ONE issue
 * with a rising count. Filter `environment:production` to read it
 * (`TPH_Reference.md` §8 — staging emits to the same project).
 *
 * 🔴 FAIL-OPEN: a Sentry client that throws must never break the page that
 * reported. 🔴 `extra` must carry no email address, no token and no value a
 * customer could paste into a URL — callers hash or truncate first. The page
 * URL and navigation breadcrumbs are query-stripped by `scrubEventUrls`.
 *
 * A no-op when no DSN is configured, since Sentry.init never ran.
 *
 * @param {Object} args
 * @param {String} args.story    e.g. '3.24'
 * @param {String} args.stage    the flow, e.g. 'cf_claim_flow'
 * @param {String} args.signal   the condition, e.g. 'claim_ended_elsewhere'
 * @param {String} args.message  constant per signal — never interpolated
 * @param {String} [args.level]  'warning' (default) or 'error'
 * @param {Object} [args.extra]  ids and counts only
 */
export const reportSignal = ({ story, stage, signal, message, level = 'warning', extra }) => {
  try {
    Sentry.captureMessage(message, {
      level,
      tags: { story, stage, signal },
      fingerprint: [`story-${story}`, stage, signal],
      extra,
    });
  } catch (e) {
    /* best-effort: the flow that reported is unaffected */
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  Sentry.setUser({ id: userId });
};

/**
 * Clears the user ID.
 */

export const clearUserId = () => {
  Sentry.setUser(null);
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  /* eslint-disable no-console */
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(
      apiErrors.map(err => ({
        status: err.status,
        code: err.code,
        details: err.details,
        ...err.meta,
      }))
    );
  }
  /* eslint-enable no-console */
};

const responseAPIErrors = error => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

const responseApiErrorInfo = err =>
  responseAPIErrors(err).map(e => ({
    status: e.status,
    code: e.code,
    meta: e.meta,
    details: e.details,
  }));

/**
 * IMD-2 — 🔴 OPERATOR-LOCKED 2026-08-25. The payment-path codes that MUST keep
 * being reported even when their payload matches an ignoreErrors entry.
 *
 * Operator rationale, verbatim: "a network failure on handleCardPayment can
 * mean the charge was created and the confirmation response was lost. Silent
 * loss on the money path is not an acceptable price for noise reduction."
 *
 * This is NOT a tidy-up list and must not be deleted by anyone shortening the
 * file. Wrapping a non-Error payload (see below) moves these sites from immune
 * to every ignoreErrors entry to subject to every one of them — ours and
 * Sentry's merged defaults alike. Carving these seven out of the wrapper's
 * `name` rule is the only thing standing between the money path and silent
 * loss. Do NOT instead edit `ignoreErrors` above: that re-opens the
 * ChunkLoadError hazard the 🔴 comment at the top of this file records.
 *
 * The sites: stripe.duck.js:41/:111/:158 and paymentMethods.duck.js:24/:52/
 * :77/:96. The eighth non-Error site — FavoriteListingsPage.duck.js:184,
 * `mark-favorites-seen-failed` — is deliberately NOT here: it is a
 * fire-and-forget UI-state write with nothing to reconcile, so a lost network
 * call there is genuinely unactionable.
 */
const PAYMENT_PATH_CODES = [
  'stripe-retrieve-payment-intent-failed',
  'stripe-handle-card-payment-failed',
  'stripe-handle-card-setup-failed',
  'create-stripe-user-failed',
  'add-payment-method-failed',
  'delete-payment-method-failed',
  'updating-payment-method-failed',
];

/**
 * Story 10.19 (AC4) — 🔴 OPERATOR-LOCKED 2026-10-12 (ruling 12, scoped in the
 * story's D12). The seller-action codes whose Sharetribe network errors MUST be
 * reported rather than dropped, as payment codes already are.
 *
 * These sites pass the RAW SDK error, which is a real `Error` (an AxiosError).
 * A real Error takes the untouched branch in `error` below, where its
 * `AxiosError: Network Error` matches the ignoreErrors entry above and is
 * dropped in the browser before it is sent. So for a code on this list a real
 * Error is routed through the `wrapNonError` path instead, and named after the
 * code — the same mechanism IMD-2 uses for a payment code — so no ignoreErrors
 * entry can match it.
 *
 * Like PAYMENT_PATH_CODES this is NOT a tidy-up list. Do NOT instead edit
 * `ignoreErrors` above: that re-opens the ChunkLoadError hazard the 🔴 comment
 * at the top of this file records.
 *
 * The site: EditListingPage.duck.js `setDraftListingTierThunk` — the
 * Sharetribe write that unlocks a draft after a tier upgrade.
 */
const SELLER_ACTION_CODES = ['set-draft-listing-tier-failed'];

/** A code whose payload must reach Sentry named after the code, never after itself. */
const isCarvedOutCode = code =>
  PAYMENT_PATH_CODES.includes(code) || SELLER_ACTION_CODES.includes(code);

/**
 * IMD-3 — a readable name is a non-empty STRING that is not the literal
 * 'Error'. storableError copies `name` straight off its source, so any ordinary
 * rejection arrives as name:'Error'; copying that through would give Sentry a
 * `type` of 'Error' and rebuild the anonymous pile under a new label.
 */
const readableName = value =>
  typeof value === 'string' && value && value !== 'Error' ? value : null;

/**
 * Wraps a non-Error payload in a real Error so it arrives at Sentry with an
 * identity of its own.
 *
 * Why this is needed: @sentry/core's eventbuilder.js returns early for anything
 * that is already an Error. Anything else is flagged synthetic, and the event's
 * `type` and stacktrace are taken from a synthetic Error minted inside
 * Scope.captureException — identical for every caller — so all such captures
 * collapse into one issue titled after the nearest named frame.
 *
 * IMD-1 — field order is LOAD-BEARING. The original message LEADS, `status`
 * comes next, and the `code` is APPENDED. ignoreErrors matches by SUBSTRING
 * against `${type}: ${value}`, so `'AxiosError: Network Error [code]'` still
 * contains the pattern `'AxiosError: Network Error'` and is dropped, whereas a
 * PREPENDED code gives `'AxiosError: code: Network Error'`, which contains it
 * nowhere. Prepending fails silently while looking correct.
 *
 * IMD-3 — the `name` decides what Sentry shows as the issue `type` and whether
 * the ignoreErrors entries can fire at all:
 *   payment code            -> the code (never the payload name; IMD-2)
 *   seller-action code      -> the code (Story 10.19, SELLER_ACTION_CODES)
 *   readable payload name   -> that name (arms the AxiosError entries)
 *   otherwise               -> the code (readable, and cannot match anything)
 *
 * ⚠️ The `code` here is the ARGUMENT to log.error, never `payload.code`.
 * stripe.duck.js's loggableError carries its own `code` key — Stripe's error
 * code — which is a different thing entirely.
 */
const wrapNonError = (payload, code) => {
  const message = typeof payload?.message === 'string' ? payload.message : '';
  const status = payload?.status;

  const wrapped = new Error(
    [message, status != null ? `(${status})` : '', `[${code}]`].filter(Boolean).join(' ')
  );

  wrapped.name = isCarvedOutCode(code) ? code : readableName(payload?.name) || code;

  return wrapped;
};

/**
 * IMD-5 — 🔴 OPERATOR-LOCKED 2026-08-25. An ALLOWLISTED projection of the
 * non-Error payload, attached as scope extra under `nonErrorPayload`.
 *
 * This REPLACES a carrier rather than adding one: eventbuilder.js already
 * serialises the whole plain-object payload as `extra.__serialized__` for every
 * plain-object capture today, and wrapping removes that. So the replacement has
 * to be narrower than what it replaces.
 *
 * Built by EXPLICIT FIELD READS — never spread-then-delete. A spread carries
 * unknown keys from a future payload shape straight through, and on this
 * projection would put `statusText` straight back.
 *
 * `statusText` is NOT projected: errors.js calls .startsWith() against it with
 * full sentences from the Marketplace API, i.e. it is an unbounded upstream
 * free-text carrier, not a fixed HTTP reason phrase. Each apiErrors entry is
 * flattened to a `${status}/${code}` STRING, dropping details/meta/source/title
 * — apiErrors is `error.data.errors` verbatim and those fields routinely echo
 * submitted values and arbitrary upstream text.
 *
 * ⚠️ scrubEventUrls never reads event.extra. There is no second line of defence
 * behind this projection, so the projection has to be the line (Story 3.10
 * AC6.4).
 */
const nonErrorPayloadProjection = payload => {
  const source = payload || {};
  const apiErrors = Array.isArray(source.apiErrors) ? source.apiErrors : [];

  return {
    type: source.type,
    name: source.name,
    message: source.message,
    status: source.status,
    apiErrors: apiErrors.map(entry => `${entry?.status}/${entry?.code}`),
  };
};

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * 🔴 Story 11.19 (4d) — `tags` is an OPTIONAL fourth argument. Everything in
 * `data` is attached as scope EXTRAS, and extras cannot be aggregated: a Sentry
 * query on an extra returns an empty string for every row. A caller that needs
 * a field to be queryable passes it here, and it is set as a scope TAG beside
 * `code`. Every value is coerced to a string, which is what a tag is.
 *
 * Callers own the cardinality decision. A tag is an aggregation key, so only a
 * value with a small, known value set belongs here: never a filename, an
 * objectKey, a URL or a token, and never free text. `sellerVideoUpload.js`'s
 * `uploadFailureTags` is the pattern: an explicit-field projection with its
 * own test.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry, as extras
 * @param {Object} [tags] low-cardinality fields to set as Sentry tags
 */
export const error = (e, code, data, tags) => {
  const apiErrors = responseApiErrorInfo(e);
  if (appSettings.sentryDsn) {
    const extra = { ...data, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setTag('code', code);
      Object.keys(tags || {}).forEach(key => {
        scope.setTag(key, String(tags[key]));
      });
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });

      // Story 10.19 — a seller-action code takes the wrap path even for a real
      // Error (SELLER_ACTION_CODES above). Every other code is unchanged.
      if (e instanceof Error && !SELLER_ACTION_CODES.includes(code)) {
        // Unchanged path. A real Error takes eventbuilder.js's early return and
        // must reach the SDK byte-for-byte as it does today: no wrapper, no
        // added extra, and no fingerprint — so scopeData.js deletes the empty
        // fingerprint key rather than grouping this event by ours.
        Sentry.captureException(e);
      } else {
        scope.setExtra('nonErrorPayload', nonErrorPayloadProjection(e));

        // Distinct exception values do NOT by themselves make distinct Sentry
        // issues — grouping is server-side and prefers the stacktrace, whose
        // top frames are still identical for every caller. The fingerprint is
        // what makes the split deterministic. '{{ default }}' stays first so
        // this can only ever split groups further, never merge ones Sentry
        // keeps apart today.
        scope.setFingerprint([
          '{{ default }}',
          code,
          e?.status != null ? String(e.status) : 'no-status',
        ]);

        Sentry.captureException(wrapNonError(e, code));
      }
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    console.error(e);
    console.error('Error code:', code, 'data:', data);
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};

const setCause = (error, cause) => {
  const seenErrors = new WeakSet();

  const setCauseIfNoExistingCause = (error, cause) => {
    if (seenErrors.has(error)) {
      return;
    }
    if (error.cause) {
      seenErrors.add(error);
      return setCauseIfNoExistingCause(error.cause, cause);
    }
    error.cause = cause;
  };

  setCauseIfNoExistingCause(error, cause);
};

export const onRecoverableError = (e, componentStack) => {
  let data = {};

  if (componentStack) {
    // Generating this synthetic error allows monitoring services to apply sourcemaps
    // to unminify the stacktrace and make it readable.
    const errorBoundaryError = new Error(e.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = componentStack;

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    setCause(e, errorBoundaryError);

    data.componentStack = componentStack;
  }

  // Replace with your error monitoring service.
  error(e, 'recoverable-error', data);
};
